import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
	DialogClose,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Loader2, AlertTriangle, CheckCircle2, StarIcon } from 'lucide-react'
import {
	requestVerificationCode,
	submitVerifiedReview,
} from '@/lib/services/feedback'
import { cn } from '@/lib/utils'
import {
	InputOTP,
	InputOTPGroup,
	InputOTPSlot,
	InputOTPSeparator,
} from '@/components/ui/input-otp'
import { useTranslation } from '@/hooks/useTranslation'

interface SubmitReviewModalProps {
	isOpen: boolean
	onClose: () => void
	programId: string
	widgetId: string
	programName: string
}

type ReviewStep = 'form' | 'verification' | 'success'

interface FormData {
	score: number | null
	reviewText: string
	name: string
	email: string
	socialLink?: string
	consentGiven: boolean
}

const initialFormData: FormData = {
	score: null,
	reviewText: '',
	name: '',
	email: '',
	socialLink: '',
	consentGiven: false,
}

// Simple Star Rating Component (can be extracted later)
interface StarRatingProps {
	rating: number | null
	setRating: (rating: number) => void
	maxStars?: number
	size?: number
	className?: string
}

const StarRatingInput: React.FC<StarRatingProps> = ({
	rating,
	setRating,
	maxStars = 5,
	size = 28, // Increased default star size
	className,
}) => {
	const [hoverRating, setHoverRating] = useState<number | null>(null)
	// starIconSize can be same as size or slightly larger as per visual preference
	const starIconSize = size

	return (
		<div
			className={cn(
				'flex items-center w-full justify-center space-x-1 md:space-x-2 py-2', // Added py-2 for vertical spacing
				className
			)}>
			{[...Array(maxStars)].map((_, index) => {
				const starValue = index + 1
				return (
					<StarIcon
						key={starValue}
						size={starIconSize}
						className={cn(
							'cursor-pointer transition-all duration-150 ease-in-out', // Smoother transition
							(hoverRating ?? rating ?? 0) >= starValue
								? 'text-yellow-400 fill-yellow-400 scale-110' // Add scale effect on selection/hover
								: 'text-gray-300 dark:text-gray-600 hover:text-yellow-300' // Hover effect for unselected
						)}
						onMouseEnter={() => setHoverRating(starValue)}
						onMouseLeave={() => setHoverRating(null)}
						onClick={() => setRating(starValue)}
					/>
				)
			})}
		</div>
	)
}

export const SubmitReviewModal: React.FC<SubmitReviewModalProps> = ({
	isOpen,
	onClose,
	programId,
	widgetId,
	programName,
}) => {
	const { t } = useTranslation()
	const [reviewStep, setReviewStep] = useState<ReviewStep>('form')
	const [formData, setFormData] = useState<FormData>(initialFormData)
	const [verificationCodeInput, setVerificationCodeInput] = useState('')
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [submissionError, setSubmissionError] = useState<string | null>(null)
	const [resendTimer, setResendTimer] = useState(0)

	useEffect(() => {
		if (isOpen) {
			// Reset form when modal opens
			setReviewStep('form')
			setFormData(initialFormData)
			setVerificationCodeInput('')
			setSubmissionError(null)
			setIsSubmitting(false)
			setResendTimer(0)
		}
	}, [isOpen])

	useEffect(() => {
		let timerId: NodeJS.Timeout
		if (resendTimer > 0) {
			timerId = setTimeout(() => {
				setResendTimer(resendTimer - 1)
			}, 1000)
		}
		return () => clearTimeout(timerId)
	}, [resendTimer])

	const handleInputChange = (
		e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
	) => {
		const { name, value } = e.target
		setFormData((prev) => ({ ...prev, [name]: value }))
	}

	const handleRatingChange = (score: number) => {
		setFormData((prev) => ({ ...prev, score }))
		if (submissionError === t('submitReviewModal.pleaseProvideStarRating')) {
			setSubmissionError(null)
		}
	}

	const handleConsentChange = (checked: boolean) => {
		setFormData((prev) => ({ ...prev, consentGiven: checked }))
		if (
			checked &&
			submissionError === t('submitReviewModal.pleaseConsent')
		) {
			setSubmissionError(null)
		}
	}

	const handleRequestCode = async (isResend: boolean = false) => {
		// Add preliminary check for widgetId and programId

		if (!programId || typeof programId !== 'string') {
			setSubmissionError(
				t('submitReviewModal.configurationError')
			)
			return
		}

		if (
			!formData.score ||
			!formData.reviewText ||
			!formData.email ||
			!formData.consentGiven
		) {
			setSubmissionError(
				t('submitReviewModal.pleaseFillAllFields')
			)
			return
		}
		setIsSubmitting(true)
		setSubmissionError(null)
		try {
			const response = await requestVerificationCode(
				{
					widgetId,
					programId,
					email: formData.email,
					score: formData.score,
					reviewText: formData.reviewText,
					name: formData.name,
					socialLink: formData.socialLink,
				},
				isResend
			)
			if (response.success) {
				setReviewStep('verification')
				setResendTimer(60) // Start 60s timer
				setSubmissionError(null) // Clear previous errors
			} else {
				setSubmissionError(
					response.message ||
						t('submitReviewModal.failedToSendCode')
				)
			}
		} catch (error: any) {
			let errorMessage = t('submitReviewModal.unexpectedError')
			if (error && typeof error.message === 'string') {
				const specificMessageMatch = String(error.message).match(
					/API Error: \d+ .+ - (.+) \((.+)\)/
				)
				if (
					specificMessageMatch &&
					specificMessageMatch[1] &&
					specificMessageMatch[2]
				) {
					errorMessage = specificMessageMatch[1]
					const errorCode = specificMessageMatch[2]

					if (errorCode === 'email_already_verified') {
						errorMessage = t('submitReviewModal.emailAlreadyVerified')
					} else if (errorCode === 'verification_code_already_sent') {
						errorMessage = t('submitReviewModal.codeAlreadySent')
					}
				} else {
					errorMessage = error.message || errorMessage
				}
			}
			setSubmissionError(errorMessage)
		}
		setIsSubmitting(false)
	}

	const handleSubmitVerifiedReview = async () => {
		if (!verificationCodeInput || verificationCodeInput.length !== 6) {
			setSubmissionError(t('submitReviewModal.pleaseEnterCode'))
			return
		}
		setIsSubmitting(true)
		setSubmissionError(null)

		try {
			await submitVerifiedReview({
				widgetId,
				programId,
				email: formData.email,
				score: formData.score ?? undefined, // API expects number | undefined
				reviewText: formData.reviewText,
				verificationCode: verificationCodeInput,
				consentGiven: formData.consentGiven,
				name: formData.name || undefined,
				socialLink: formData.socialLink || undefined,
				userMetadata: {
					pageUrl: window.location.href,
					userAgent: navigator.userAgent,
					submittedFrom: 'PublicReviewPageModal',
				},
				source: 'public_review_page',
			})
			setReviewStep('success')
			setSubmissionError(null)
		} catch (error: any) {
			let errorMessage = t('submitReviewModal.failedToSubmit')
			if (error && typeof error.message === 'string') {
				const specificMessageMatch = String(error.message).match(
					/API Error: \d+ .+ - (.+) \((.+)\)/
				)
				if (
					specificMessageMatch &&
					specificMessageMatch[1] &&
					specificMessageMatch[2]
				) {
					errorMessage = specificMessageMatch[1]
					const errorCode = specificMessageMatch[2]

					if (errorCode === 'invalid_code') {
						errorMessage = t('submitReviewModal.invalidCode')
						setVerificationCodeInput('')
					} else if (errorCode === 'code_expired') {
						errorMessage = t('submitReviewModal.codeExpired')
						setReviewStep('form')
						setVerificationCodeInput('')
						setResendTimer(0)
					}
				} else {
					errorMessage = error.message || errorMessage
				}
			}
			setSubmissionError(errorMessage)
			// Optionally, keep them on verification step for some errors, or move to error step
			// For 'code_expired', we moved to 'form'. For 'invalid_code', they stay on 'verification'.
		}
		setIsSubmitting(false)
	}

	const renderFormStep = () => {
		const isContinueDisabled =
			isSubmitting ||
			!formData.score ||
			!formData.reviewText.trim() ||
			!formData.email.trim() ||
			!formData.consentGiven
		return (
			<>
				<DialogHeader>
					<DialogTitle>
						{t('submitReviewModal.title')}{' '}
						<span className="text-primary">{programName}</span>
					</DialogTitle>
					<DialogDescription>
						{t('submitReviewModal.description')}
					</DialogDescription>
				</DialogHeader>
				<div className="space-y-4 py-4">
					{submissionError && reviewStep === 'form' && (
						<Alert variant="destructive">
							<AlertTriangle className="h-4 w-4" />
							<AlertTitle>{t('submitReviewModal.error')}</AlertTitle>
							<AlertDescription>{submissionError}</AlertDescription>
						</Alert>
					)}
					<div className="space-y-1">
						<Label htmlFor="score" className="font-medium">
							{t('submitReviewModal.yourRating')}
						</Label>
						<StarRatingInput
							rating={formData.score}
							setRating={handleRatingChange}
							size={48} // Larger stars
						/>
					</div>
					<div className="space-y-1">
						<Label htmlFor="reviewText" className="font-medium">
							{t('submitReviewModal.yourReview')}
						</Label>
						<Textarea
							id="reviewText"
							name="reviewText"
							value={formData.reviewText}
							onChange={handleInputChange}
							placeholder={t('submitReviewModal.reviewPlaceholder')}
							rows={5}
							required
							className="resize-none"
						/>
					</div>

					<div className="pt-2 space-y-4">
						<div className="space-y-1">
							<Label htmlFor="name">{t('submitReviewModal.fullName')}</Label>
							<Input
								id="name"
								name="name"
								value={formData.name}
								onChange={handleInputChange}
								placeholder={t('submitReviewModal.namePlaceholder')}
							/>
						</div>
						<div className="space-y-1">
							<Label htmlFor="email">{t('submitReviewModal.emailAddress')}</Label>
							<Input
								id="email"
								name="email"
								type="email"
								value={formData.email}
								onChange={handleInputChange}
								placeholder={t('submitReviewModal.emailPlaceholder')}
								required
							/>
							<p className="text-xs text-muted-foreground">
								{t('submitReviewModal.emailNotDisplayed')}
							</p>
						</div>
						<div className="space-y-1">
							<Label htmlFor="socialLink">{t('submitReviewModal.socialProfileLink')}</Label>
							<Input
								id="socialLink"
								name="socialLink"
								type="url"
								value={formData.socialLink || ''}
								onChange={handleInputChange}
								placeholder={t('submitReviewModal.socialLinkPlaceholder')}
							/>
						</div>
					</div>

					<div className="flex items-start space-x-2 pt-3">
						<Checkbox
							id="consentGiven"
							name="consentGiven"
							checked={formData.consentGiven}
							onCheckedChange={handleConsentChange}
							className="mt-0.5"
						/>
						<Label
							htmlFor="consentGiven"
							className="text-sm font-normal text-gray-600 dark:text-gray-400 cursor-pointer">
							{t('submitReviewModal.consent')}
						</Label>
					</div>
				</div>
				<DialogFooter className="mt-2">
					<DialogClose asChild>
						<Button variant="outline" onClick={onClose} disabled={isSubmitting}>
							{t('submitReviewModal.cancel')}
						</Button>
					</DialogClose>
					<Button
						onClick={() => handleRequestCode(false)}
						disabled={isContinueDisabled}>
						{isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
						{t('submitReviewModal.continue')}
					</Button>
				</DialogFooter>
			</>
		)
	}

	const renderVerificationStep = () => {
		const isVerifyDisabled =
			isSubmitting ||
			!verificationCodeInput ||
			verificationCodeInput.length !== 6

		return (
			<>
				<DialogHeader>
					<DialogTitle>{t('submitReviewModal.verifyYourEmail')}</DialogTitle>
					<DialogDescription>
						{t('submitReviewModal.verificationCodeSent')}{' '}
						<strong className="text-gray-900 dark:text-gray-100">
							{formData.email}
						</strong>
						.
						<br />
						{t('submitReviewModal.verificationInstructions')}
					</DialogDescription>
				</DialogHeader>
				<div className="space-y-6 py-4">
					{submissionError && reviewStep === 'verification' && (
						<Alert variant="destructive">
							<AlertTriangle className="h-4 w-4" />
							<AlertTitle>{t('submitReviewModal.error')}</AlertTitle>
							<AlertDescription>{submissionError}</AlertDescription>
						</Alert>
					)}
					<div className="space-y-2">
						<Label htmlFor="verificationCodeInput" className="font-medium">
							{t('submitReviewModal.verificationCode')}
						</Label>
						<InputOTP
							maxLength={6}
							value={verificationCodeInput}
							onChange={setVerificationCodeInput}>
							<InputOTPGroup className="w-full justify-center">
								<InputOTPSlot index={0} />
								<InputOTPSlot index={1} />
								<InputOTPSlot index={2} />
								<InputOTPSeparator className="hidden sm:block" />
								<InputOTPSlot index={3} />
								<InputOTPSlot index={4} />
								<InputOTPSlot index={5} />
							</InputOTPGroup>
						</InputOTP>
					</div>
					<div className="text-sm text-center space-y-2">
						<div>
							{t('submitReviewModal.didntReceiveCode')}{' '}
							<Button
								variant="link"
								size="sm"
								className="p-0 h-auto text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300 disabled:text-gray-400"
								onClick={() => handleRequestCode(true)}
								disabled={isSubmitting || resendTimer > 0}>
								{t('submitReviewModal.resendCode')}
								{resendTimer > 0 ? ` ${t('submitReviewModal.resendIn', { seconds: resendTimer })}` : ''}
							</Button>
						</div>
						<div>
							{t('submitReviewModal.or')}{' '}
							<Button
								variant="link"
								size="sm"
								className="p-0 h-auto text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300"
								onClick={() => {
									setReviewStep('form')
									setSubmissionError(null)
								}}
								disabled={isSubmitting}>
								{t('submitReviewModal.changeEmail')}
							</Button>
						</div>
					</div>
				</div>
				<DialogFooter className="mt-2">
					<Button
						variant="outline"
						onClick={() => {
							setReviewStep('form')
							setSubmissionError(null)
						}}
						disabled={isSubmitting}>
						{t('submitReviewModal.back')}
					</Button>
					<Button
						onClick={handleSubmitVerifiedReview}
						disabled={isVerifyDisabled}>
						{isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
						{t('submitReviewModal.verifyAndSubmit')}
					</Button>
				</DialogFooter>
			</>
		)
	}

	const renderSuccessStep = () => (
		<>
			<DialogHeader className="items-center text-center py-4">
				<CheckCircle2 className="h-16 w-16 text-green-500 mb-4" />
				<DialogTitle className="text-2xl">{t('submitReviewModal.reviewSubmitted')}</DialogTitle>
				<DialogDescription className="text-base">
					{t('submitReviewModal.thankYou')}{' '}
					<strong className="text-gray-900 dark:text-gray-100">
						{programName}
					</strong>
					.
					<br />
					{t('submitReviewModal.reviewSubmittedSuccess')}
				</DialogDescription>
			</DialogHeader>
			<DialogFooter className="sm:justify-center pt-2 pb-4">
				<Button onClick={onClose} className="px-6">
					{t('submitReviewModal.done')}
				</Button>
			</DialogFooter>
		</>
	)

	return (
		<Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
			<DialogContent className="sm:max-w-md md:max-w-lg dark:bg-gray-800 max-h-[90vh] overflow-y-auto">
				{reviewStep === 'form' && renderFormStep()}
				{reviewStep === 'verification' && renderVerificationStep()}
				{reviewStep === 'success' && renderSuccessStep()}
			</DialogContent>
		</Dialog>
	)
}
